import React, { useContext, useEffect, useMemo, useState } from "react";
import { Box, Tab, Tabs, Typography } from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import { makeStyles } from "@material-ui/core/styles";
import { useHistory, useLocation } from "react-router-dom";

import Agenda from "../Agenda";
import Schedules from "../Schedules";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";

const VIEW_APPOINTMENTS = "appointments";
const VIEW_MESSAGES = "messages";
const useStyles = makeStyles(theme => ({
  wrapper: { display: "flex", flexDirection: "column", height: "100%", minHeight: 0 },
  tabs: { flex: "0 0 auto", padding: "0 16px", borderBottom: `1px solid ${theme.palette.divider}` },
  content: { flex: "1 1 0", minHeight: 0, "& > .MuiContainer-root": { height: "100% !important", minHeight: "0 !important", flex: "1 1 0" } },
}));

const withView = (search, view) => {
  const params = new URLSearchParams(search);
  params.set("view", view);
  return `?${params.toString()}`;
};

const UnifiedAgenda = ({ defaultView = VIEW_APPOINTMENTS }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const history = useHistory();
  const location = useLocation();
  const { getPlanCompany } = usePlans();
  const isAdmin = user?.profile === "admin";
  const isSuper = Boolean(user?.super);
  const companyId = user?.companyId;
  const requestedView = new URLSearchParams(location.search).get("view");

  const [planReady, setPlanReady] = useState(isSuper);
  const [canUseMessages, setCanUseMessages] = useState(isSuper);

  useEffect(() => {
    let active = true;

    if (isSuper) {
      setCanUseMessages(true);
      setPlanReady(true);
      return () => {
        active = false;
      };
    }

    setPlanReady(false);
    setCanUseMessages(false);
    if (!companyId) {
      setPlanReady(true);
      return () => {
        active = false;
      };
    }

    getPlanCompany(undefined, companyId)
      .then((planConfigs) => {
        if (active) {
          setCanUseMessages(Boolean(planConfigs?.plan?.useSchedules));
          setPlanReady(true);
        }
      })
      .catch(() => {
        // A failed plan lookup must not grant access to scheduled messages.
        if (active) {
          setCanUseMessages(false);
          setPlanReady(true);
        }
      });

    return () => {
      active = false;
    };
    // getPlanCompany is recreated by usePlans on each render; company and role
    // are the inputs that determine which plan must be checked.
  }, [companyId, isSuper]);

  const availableViews = useMemo(() => {
    const views = [];
    if (isAdmin) views.push(VIEW_APPOINTMENTS);
    if (planReady && canUseMessages) views.push(VIEW_MESSAGES);
    return views;
  }, [isAdmin, planReady, canUseMessages]);

  const selectedView = (() => {
    if (location.pathname.startsWith("/schedules") && !requestedView) return null;
    if (requestedView === VIEW_APPOINTMENTS) return isAdmin ? VIEW_APPOINTMENTS : null;
    if (requestedView === VIEW_MESSAGES) {
      return planReady && canUseMessages ? VIEW_MESSAGES : null;
    }
    if (requestedView && availableViews.includes(requestedView)) return requestedView;
    if (availableViews.includes(defaultView)) return defaultView;
    return availableViews[0] || null;
  })();

  useEffect(() => {
    if (!location.pathname.startsWith("/schedules") || requestedView) return;
    history.replace({
      pathname: location.pathname,
      search: withView(location.search, VIEW_MESSAGES),
      hash: location.hash,
      state: location.state,
    });
  }, [history, location.hash, location.pathname, location.search, location.state, requestedView]);

  useEffect(() => {
    if (location.pathname.startsWith("/schedules") || requestedView || !planReady || !selectedView) return;
    history.replace({
      pathname: location.pathname,
      search: withView(location.search, selectedView),
      hash: location.hash,
      state: location.state,
    });
  }, [history, location.hash, location.pathname, location.search, location.state, planReady, requestedView, selectedView]);

  useEffect(() => {
    if (!requestedView || !planReady) return;
    const isUnavailable =
      (requestedView === VIEW_APPOINTMENTS && !isAdmin) ||
      (requestedView === VIEW_MESSAGES && !canUseMessages);
    if (!isUnavailable) return;

    const fallbackView = availableViews[0];
    if (!fallbackView) return;
    history.replace({
      pathname: location.pathname,
      search: withView(location.search, fallbackView),
      hash: location.hash,
      state: location.state,
    });
  }, [
    availableViews,
    canUseMessages,
    history,
    isAdmin,
    location.hash,
    location.pathname,
    location.search,
    location.state,
    planReady,
    requestedView,
  ]);

  const changeView = (_event, nextView) => {
    history.push({
      pathname: location.pathname,
      search: withView(location.search, nextView),
      hash: location.hash,
      state: location.state,
    });
  };

  return (
    <Box className={classes.wrapper}>
      <Tabs
        value={selectedView || false}
        onChange={changeView}
        aria-label="Agenda"
        className={classes.tabs}
        indicatorColor="primary"
        textColor="primary"
      >
        {isAdmin && <Tab value={VIEW_APPOINTMENTS} label="Compromissos" />}
        {planReady && canUseMessages && (
          <Tab value={VIEW_MESSAGES} label="Mensagens agendadas" />
        )}
      </Tabs>

      {selectedView ? (
        <Box className={classes.content}>
          {selectedView === VIEW_APPOINTMENTS && isAdmin && <Agenda />}
          {selectedView === VIEW_MESSAGES && planReady && canUseMessages && <Schedules />}
        </Box>
      ) : (
        <Box p={3}>
          {!planReady ? (
            <Typography color="textSecondary">Verificando acesso à agenda…</Typography>
          ) : (
            <Alert severity="warning">
              Esta empresa não possui permissão para acessar a agenda.
            </Alert>
          )}
        </Box>
      )}
    </Box>
  );
};

export const LegacySchedules = () => <UnifiedAgenda defaultView={VIEW_MESSAGES} />;

export default UnifiedAgenda;

